import type { BallState, ShotInput, ShotResult } from "@shared/physics";

type ClaimResponse =
  | { ok: true; handle: string; countryCode: string }
  | { ok: false; reason: string };

type SignInResponse = ClaimResponse;

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  try {
    return (await res.json()) as T;
  } catch {
    throw new Error("Bad response");
  }
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path);
  try {
    return (await res.json()) as T;
  } catch {
    throw new Error("Bad response");
  }
}

export async function apiClaim(
  handle: string,
  token: string,
): Promise<ClaimResponse> {
  return postJson<ClaimResponse>("/api/handle/claim", { handle, token });
}

export async function apiSignIn(
  handle: string,
  token: string,
): Promise<SignInResponse> {
  return postJson<SignInResponse>("/api/handle/signin", { handle, token });
}

export async function apiHandleAvailable(handle: string): Promise<boolean> {
  try {
    const res = await fetch(
      `/api/handle/available?handle=${encodeURIComponent(handle)}`,
    );
    if (!res.ok) return false;
    const data = (await res.json()) as { available?: boolean };
    return Boolean(data.available);
  } catch {
    return false;
  }
}

export type ShotSubmitResponse =
  | {
      ok: true;
      result: ShotResult;
      strokes: number;
      holeComplete: boolean;
      roundComplete: boolean;
      ballState: BallState;
    }
  | { ok: false; reason: string };

export async function apiSubmitShot(
  handle: string,
  token: string,
  courseDate: string,
  hole: number,
  aim: ShotInput,
): Promise<ShotSubmitResponse> {
  return postJson<ShotSubmitResponse>("/api/shot/submit", {
    handle,
    token,
    courseDate,
    hole,
    aim,
  });
}

export type RoundStateResponse =
  | {
      ok: true;
      state: "new" | "resume" | "complete";
      courseDate: string;
      currentHole: number;
      strokesPerHole: number[];
      ballPosition: { x: number; z: number } | null;
      completed: {
        totalStrokes: number;
        rank: number | null;
        totalPlayers: number;
      } | null;
    }
  | { ok: false; reason: string };

export async function apiGetRound(
  handle: string,
  token: string,
  courseDate: string,
): Promise<RoundStateResponse> {
  return postJson<RoundStateResponse>("/api/round", {
    handle,
    token,
    courseDate,
  });
}

export type CompleteResponse =
  | {
      ok: true;
      totalStrokes: number;
      rank: number | null;
      totalPlayers: number;
      perHole: number[];
    }
  | { ok: false; reason: string };

export async function apiCompleteRound(
  handle: string,
  token: string,
  courseDate: string,
): Promise<CompleteResponse> {
  return postJson<CompleteResponse>("/api/score/complete", {
    handle,
    token,
    courseDate,
  });
}

export type LeaderboardEntry = {
  handle: string;
  countryCode: string;
  totalStrokes: number;
  completedAt: number;
};

export type LeaderboardResponse = {
  ok: boolean;
  courseDate: string;
  entries: LeaderboardEntry[];
};

export async function apiLeaderboard(
  courseDate?: string,
  limit = 50,
): Promise<LeaderboardResponse> {
  const params = new URLSearchParams();
  if (courseDate) params.set("courseDate", courseDate);
  params.set("limit", String(limit));
  return getJson<LeaderboardResponse>(
    `/api/leaderboard/today?${params.toString()}`,
  );
}

export type GhostShot = {
  handle: string;
  countryCode: string;
  strokes: number;
  outcome: string;
  samples: { t: number; x: number; z: number }[];
  createdAt: number;
};

export type GhostShotsResponse = {
  ok: boolean;
  courseDate: string;
  hole: number;
  shots: GhostShot[];
};

export async function apiGhostShots(
  holeIndex: number,
  courseDate?: string,
  limit = 20,
): Promise<GhostShotsResponse> {
  const params = new URLSearchParams();
  if (courseDate) params.set("courseDate", courseDate);
  params.set("limit", String(limit));
  return getJson<GhostShotsResponse>(
    `/api/shots/${holeIndex}?${params.toString()}`,
  );
}

export type LiveEventOutcome = "holed" | "out_of_bounds" | "comet_hit";

export type LiveEvent = {
  id: string;
  handle: string;
  countryCode: string;
  holeIndex: number;
  strokes: number;
  /** Server-provided shot outcome. Defaults to "holed" for older
   *  responses that pre-date the field, so the ticker stays correct
   *  during a deploy gap. */
  outcome?: LiveEventOutcome;
  createdAt: number;
};

export type EventsResponse = {
  ok: boolean;
  courseDate: string;
  events: LiveEvent[];
};

export async function apiEvents(
  courseDate: string,
  since: number,
  limit = 20,
): Promise<EventsResponse> {
  const params = new URLSearchParams();
  params.set("courseDate", courseDate);
  params.set("since", String(since));
  params.set("limit", String(limit));
  return getJson<EventsResponse>(`/api/events?${params.toString()}`);
}

export type PresenceResponse = {
  ok: boolean;
  active: number;
  /** Histogram parallel to course holes — perHole[i] is the number
   *  of players whose current (lowest-incomplete) hole is i. */
  perHole?: number[];
};

export async function apiPresence(): Promise<PresenceResponse> {
  return getJson<PresenceResponse>(`/api/presence`);
}

export type LiveRoundEntry = {
  handle: string;
  countryCode: string;
  currentHole: number;
  holesCompleted: number;
  totalStrokes: number;
  lastActiveAt: number;
};

export type LiveLeaderboardResponse = {
  ok: boolean;
  courseDate: string;
  entries: LiveRoundEntry[];
};

export async function apiLiveLeaderboard(
  courseDate?: string,
  limit = 30,
): Promise<LiveLeaderboardResponse> {
  const params = new URLSearchParams();
  if (courseDate) params.set("courseDate", courseDate);
  params.set("limit", String(limit));
  return getJson<LiveLeaderboardResponse>(
    `/api/leaderboard/live?${params.toString()}`,
  );
}

export type ReactionEmoji = "clap" | "fire" | "skull";

export type ReactionEntry = {
  handle: string;
  emoji: ReactionEmoji;
  count: number;
};

export type ReactionEvent = {
  handle: string;
  emoji: ReactionEmoji;
  ts: number;
};

export type ReactionsResponse = {
  ok: boolean;
  courseDate: string;
  entries: ReactionEntry[];
  events: ReactionEvent[];
  emojis: readonly ReactionEmoji[];
  serverTime: number;
};

export async function apiReactions(
  courseDate?: string,
  since?: number,
): Promise<ReactionsResponse> {
  const params = new URLSearchParams();
  if (courseDate) params.set("courseDate", courseDate);
  if (since !== undefined) params.set("since", String(since));
  return getJson<ReactionsResponse>(
    `/api/reactions?${params.toString()}`,
  );
}

export async function apiReact(
  courseDate: string,
  handle: string,
  emoji: ReactionEmoji,
): Promise<{ ok: boolean; count?: number; reason?: string }> {
  try {
    const r = await fetch("/api/react", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ courseDate, handle, emoji }),
    });
    return (await r.json()) as { ok: boolean; count?: number; reason?: string };
  } catch {
    return { ok: false, reason: "network" };
  }
}

export type SpectatorShot = {
  holeIndex: number;
  strokes: number;
  outcome: string;
  aim: { aimX: number; aimZ: number; power: number };
  samples: { t: number; x: number; z: number }[];
  createdAt: number;
};

export type SpectatorRoundResponse =
  | {
      ok: true;
      handle: string;
      countryCode: string;
      courseDate: string;
      shots: SpectatorShot[];
    }
  | { ok: false; reason: string };

export async function apiRoundReplay(
  handle: string,
  courseDate: string,
): Promise<SpectatorRoundResponse> {
  try {
    // `cache: "no-store"` so that an earlier failure (e.g. before
    // the schema migration shipped) doesn't get served from the HTTP
    // cache forever. Round payload is fresh-data anyway — no benefit
    // from cache here.
    const r = await fetch(
      `/api/round/${encodeURIComponent(handle)}/${encodeURIComponent(courseDate)}`,
      { cache: "no-store" },
    );
    return (await r.json()) as SpectatorRoundResponse;
  } catch {
    return { ok: false, reason: "network" };
  }
}

export type CountryLeaderboardEntry = {
  countryCode: string;
  rounds: number;
  avgStrokes: number;
  bestStrokes: number;
};

export type CountryLeaderboardResponse = {
  ok: boolean;
  courseDate: string;
  entries: CountryLeaderboardEntry[];
};

export async function apiCountryLeaderboard(
  courseDate?: string,
  limit = 25,
): Promise<CountryLeaderboardResponse> {
  const params = new URLSearchParams();
  if (courseDate) params.set("courseDate", courseDate);
  params.set("limit", String(limit));
  return getJson<CountryLeaderboardResponse>(
    `/api/leaderboard/country?${params.toString()}`,
  );
}

export type LobbyJoin = {
  handle: string;
  countryCode: string;
  joinedAt: number;
};

export type LobbyResponse = {
  ok: boolean;
  active: number;
  joins: LobbyJoin[];
  yesterday: {
    courseDate: string;
    handle: string;
    countryCode: string | null;
    totalStrokes: number;
  } | null;
  serverTime: number;
};

export async function apiLobby(since: number): Promise<LobbyResponse> {
  const params = new URLSearchParams();
  params.set("since", String(since));
  return getJson<LobbyResponse>(`/api/lobby?${params.toString()}`);
}

export type HofEntry = {
  handle: string;
  countryCode: string;
  bestStrokes: number;
  bestDate: string;
  rounds: number;
};

export type HofResponse = { ok: boolean; entries: HofEntry[] };

export async function apiHallOfFame(limit = 100): Promise<HofResponse> {
  return getJson<HofResponse>(`/api/leaderboard/hof?limit=${limit}`);
}

export type StreakEntry = {
  handle: string;
  countryCode: string;
  longest: number;
  current: number;
  rounds: number;
};

export type StreakResponse = { ok: boolean; entries: StreakEntry[] };

export async function apiStreaks(limit = 100): Promise<StreakResponse> {
  return getJson<StreakResponse>(`/api/leaderboard/streaks?limit=${limit}`);
}
